/**
 * PersonTracker — IoU + centroid-based multi-object tracker for the
 * pickleball doubles capture pipeline. Inspired by ByteTrack (lite).
 *
 * Why: MediaPipe's pose landmarker returns frame-by-frame detections with
 * no concept of "this is the same person as last frame". When two players
 * cross visually, slot assignments scramble. This tracker maintains a
 * persistent ID for each detected person across frames, surviving:
 *   • brief occlusions (player walks behind another) — predicted forward
 *     for up to MAX_LOST_FRAMES before the track is killed
 *   • crossovers — the ID stays with the same person via velocity-aware
 *     IoU matching
 *
 * Coach taps a player to lock their tracker ID to a slot. As long as the
 * track survives, the slot follows it — no more "slot 2 just jumped to
 * the player who walked across".
 */

const MAX_LOST_FRAMES = 30;       // ~1 second at 30fps before a track dies
const IOU_GATE = 0.20;            // min IoU to consider a match
const CENTROID_GATE_REL = 0.18;   // backup centroid distance, fraction of frame diag

function iou(a, b) {
  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  const x2 = Math.min(a.x + a.w, b.x + b.w);
  const y2 = Math.min(a.y + a.h, b.y + b.h);
  if (x2 <= x1 || y2 <= y1) return 0;
  const inter = (x2 - x1) * (y2 - y1);
  const union = a.w * a.h + b.w * b.h - inter;
  return union > 0 ? inter / union : 0;
}

function bboxCenter(b) {
  return { x: b.x + b.w / 2, y: b.y + b.h / 2 };
}

function centroidDist(a, b) {
  const ca = bboxCenter(a);
  const cb = bboxCenter(b);
  return Math.hypot(ca.x - cb.x, ca.y - cb.y);
}

export class PersonTracker {
  constructor() {
    this._tracks = [];     // [{ id, bbox, vx, vy, age, lost, confidence }]
    this._nextId = 1;
  }

  /** Active (non-lost) tracks the UI can iterate over. */
  get tracks() {
    return this._tracks.filter((t) => t.lost < MAX_LOST_FRAMES);
  }

  /** Look up a track by id. */
  byId(id) {
    return this._tracks.find((t) => t.id === id);
  }

  /**
   * Update with one frame's detections.
   * @param {Array} detections - [{x, y, w, h, confidence}] in NORMALIZED
   *   frame coordinates (0..1).
   * @returns Array of tracks with .matched_detection set (or null if
   *   the track was predicted forward this frame).
   */
  update(detections) {
    // 1. Predict forward — every track gets advanced by its velocity.
    for (const t of this._tracks) {
      t.predicted = {
        x: t.bbox.x + (t.vx || 0),
        y: t.bbox.y + (t.vy || 0),
        w: t.bbox.w,
        h: t.bbox.h,
      };
      t.matched_detection = null;
    }

    // 2. Greedy assignment — iterate tracks in confidence order, pick
    //    best IoU match from unassigned detections. Falls back to centroid
    //    distance for low-IoU edge cases (small fast-moving objects).
    const used = new Set();
    const sorted = [...this._tracks].sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
    for (const t of sorted) {
      let bestIdx = -1;
      let bestScore = 0;
      for (let i = 0; i < detections.length; i++) {
        if (used.has(i)) continue;
        const d = detections[i];
        const ov = iou(t.predicted, d);
        if (ov > bestScore && ov >= IOU_GATE) {
          bestScore = ov;
          bestIdx = i;
        }
      }
      // Fallback: small/distant players give tiny IoU — use centroid
      if (bestIdx === -1) {
        let bestD = CENTROID_GATE_REL;
        for (let i = 0; i < detections.length; i++) {
          if (used.has(i)) continue;
          const d = detections[i];
          const dd = centroidDist(t.predicted, d);
          // Scale gate by predicted bbox height — bigger players move
          // more in absolute terms but proportionally similar
          const gate = Math.max(0.05, t.predicted.h * 0.9);
          if (dd < gate && dd < bestD) {
            bestD = dd;
            bestIdx = i;
          }
        }
      }
      if (bestIdx !== -1) {
        used.add(bestIdx);
        const d = detections[bestIdx];
        // Update velocity (EMA smoothing) before clobbering bbox
        const alpha = 0.45;
        const rawVx = d.x + d.w / 2 - (t.bbox.x + t.bbox.w / 2);
        const rawVy = d.y + d.h / 2 - (t.bbox.y + t.bbox.h / 2);
        t.vx = alpha * rawVx + (1 - alpha) * (t.vx || 0);
        t.vy = alpha * rawVy + (1 - alpha) * (t.vy || 0);
        t.bbox = { x: d.x, y: d.y, w: d.w, h: d.h };
        t.confidence = d.confidence;
        t.lost = 0;
        t.age = (t.age || 0) + 1;
        t.matched_detection = d;
      } else {
        // Track wasn't matched this frame — predict forward, increment lost
        t.bbox = t.predicted;
        t.lost = (t.lost || 0) + 1;
      }
    }

    // 3. Spawn new tracks for unmatched detections
    for (let i = 0; i < detections.length; i++) {
      if (used.has(i)) continue;
      const d = detections[i];
      this._tracks.push({
        id: this._nextId++,
        bbox: { x: d.x, y: d.y, w: d.w, h: d.h },
        vx: 0,
        vy: 0,
        age: 1,
        lost: 0,
        confidence: d.confidence,
        matched_detection: d,
      });
    }

    // 4. Reap tracks that have been lost too long
    this._tracks = this._tracks.filter((t) => t.lost < MAX_LOST_FRAMES);

    return this.tracks;
  }

  reset() {
    this._tracks = [];
    this._nextId = 1;
  }
}
